import { useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTenant } from '@/providers/TenantProvider'
import { useStorageTransfer } from '@/hooks/useStorageTransfers'
import { TransferPrintSheet } from '@/components/inventory/storage/transfer-print/TransferPrintSheet'

/**
 * `/inventory/storage-transfers/:transferId/print` (user request, Task 11).
 *
 * Inside the authenticated routes but outside Layout, so the page is the
 * sheet alone — no sidebar or top bar to hide from the printer. Same pattern
 * as the purchase-order print view (`PurchaseOrderPrintPage`, z8nrz7f7dz): the
 * one toolbar is `transfer-print-no-print`, hidden by the `@media print` rule
 * below.
 *
 * Opened in its own tab from the plan page's Print button, so it waits for the
 * tenant the way Layout does — every request here is tenant-scoped.
 */
export function StorageTransferPrintPage() {
  const { isTenantResolved } = useTenant()
  if (!isTenantResolved) return <Centered />
  return <PrintView />
}

function PrintView() {
  const { transferId = '' } = useParams<{ transferId: string }>()
  const transfer = useStorageTransfer(transferId)
  // Below the sheet's per-move QR cap, this starts false and flips once every
  // row's code has settled, so Print can't be clicked on a sheet still
  // half-drawn (fix round 1). `TransferPrintSheet` reports `true` straight
  // away when there's nothing to wait for (no moves, or past the cap).
  const [printReady, setPrintReady] = useState(false)

  if (transfer.isLoading) return <Centered />
  if (!transfer.data) {
    return (
      <Centered>
        <p className='text-sm text-muted-foreground'>Plan not found.</p>
      </Centered>
    )
  }

  const t = transfer.data
  // Both codes travel on the payload, same as the plan page — matching a root
  // id against every storage root just to show "ULT-01 → ULT-02" is work this
  // page does not need to redo.
  const sourceCode = t.sourceRoot?.code ?? t.moves[0]?.sourceNodePath.split('/')[0] ?? 'Source'
  const targetCode = t.targetRoot?.code ?? t.moves[0]?.targetNodePath?.split('/')[0] ?? 'Target'

  return (
    <div className='min-h-screen bg-gray-100 print:bg-white'>
      <style>{'@media print { .transfer-print-no-print { display: none !important; } }'}</style>
      <div className='transfer-print-no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white px-6 py-3'>
        <p className='text-sm font-medium text-gray-900'>
          {sourceCode} → {targetCode}
        </p>
        <Button size='sm' className='gap-1' onClick={() => window.print()} disabled={!printReady}>
          {printReady ? <Printer className='h-4 w-4' /> : <Loader2 className='h-4 w-4 animate-spin' />}
          {printReady ? 'Print' : 'Preparing codes…'}
        </Button>
      </div>
      <div className='py-8 print:py-0'>
        <div className='mx-auto max-w-[800px] bg-white px-10 py-8 shadow-sm print:max-w-none print:px-0 print:py-0 print:shadow-none'>
          <TransferPrintSheet
            transfer={t}
            sourceCode={sourceCode}
            targetCode={targetCode}
            onReadyChange={setPrintReady}
          />
        </div>
      </div>
    </div>
  )
}

function Centered({ children }: { children?: ReactNode }) {
  return (
    <div className='flex h-screen items-center justify-center bg-background'>
      {children ?? <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />}
    </div>
  )
}
